import { DatePipe } from '@angular/common'
import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs'
import { ConfirmationService } from 'primeng/api'
import { $app } from '../../../app.service'
import { AppBaseComponent } from '../../../shared/components/app-base.component'
import { AppProject } from '../../../shared/models/interfaces/app-project.interface'
import { AppReportingPeriodRequest, AppReportingPeriodResponse } from '../../../shared/models/interfaces/app-reporting-period.interface'
import { AppEditProjectRequest } from '../../../shared/models/interfaces/app-edit-project-request.interface'
import { AppBasicConfirmDialogComponent } from '../../../shared/components/app-basic-confirm-dialog/app-basic-confirm-dialog.component'
import { EpRunningReportingPeriodComponent } from './ep-running-reporting-period/ep-running-reporting-period.component'
import { EpRunningLogoComponent } from './ep-running-logo/ep-running-logo.component'
import { ButtonModule } from 'primeng/button'
import { EpRunningDetailTabComponent } from './ep-running-detail-tab/ep-running-detail-tab.component'

@Component({
   selector: 'ep-running',
   templateUrl: './ep-running.component.html',
   providers: [ConfirmationService],
   standalone: true,
   imports: [
      MatTabsModule,
      EpRunningDetailTabComponent,
      ButtonModule,
      EpRunningLogoComponent,
      EpRunningReportingPeriodComponent,
      AppBasicConfirmDialogComponent
   ]
})
export class EpRunningComponent extends AppBaseComponent {
   @Input() tabIndex?: string | null
   @Input() project?: AppProject | null

   @Input() isLoadingReportingPeriods?: boolean | null
   @Input() reportingPeriodsRes?: AppReportingPeriodResponse | null

   @Input() logo?: string | null
   @Input() isLoadingLogo?: boolean | null

   titleFC?: FormControl
   acronymFC?: FormControl
   endDateFC?: FormControl
   foundingFC?: FormControl
   durationFC?: FormControl
   startDateFC?: FormControl
   typeOfActionFC?: FormControl
   currentMonthFC?: FormControl
   fundingAgreementIdFC?: FormControl
   callOfTheProposalFC?: FormControl
   generalObjectivesFC?: FormControl
   fundingAssociatedNameFC?: FormControl

   editProjectFrom?: FormGroup

   reportingEndMonthFC?: FormControl
   reportingNextPeriodFC?: FormControl
   reportingNextStartMonthFC?: FormControl

   typeOfActionArray = [
      { id: 'RIA', name: 'RIA - Research & Innovation Action' },
      { id: 'IA', name: 'IA - Innovation Action' },
      { id: 'CSA', name: 'CSA - Coordination & Support Action' },
      { id: 'OTHER', name: 'Other' }
   ]

   _hasUnsavedChanges?: boolean

   @Output() deleteReportClick = new EventEmitter()
   @Output() doActionDeleteLogo = new EventEmitter()
   @Output() doActionSaveLogo = new EventEmitter<string>()
   @Output() hasUnsavedChanges = new EventEmitter<boolean>()
   @Output() addReportClick = new EventEmitter<AppReportingPeriodRequest>()
   @Output() updateProjectDetailClick = new EventEmitter<AppEditProjectRequest>()

   @ViewChild('matTabGroup') matTabGroup?: MatTabGroup

   constructor(
      private _date: DatePipe,
      private _confirmationService: ConfirmationService
   ) {
      super()
      this.initListener()
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['project'] && this.project) {
         this.initEditForm(this.project)
         this.initReportingPeriodForm()
      }

      if (changes['reportingPeriodsRes'] && this.reportingPeriodsRes) {
         this.initReportingPeriodForm()
      }
   }

   ngAfterViewInit(): void {
      if (this.tabIndex && this.matTabGroup) {
         this.matTabGroup.selectedIndex = Number(this.tabIndex)
      }
   }

   doActionAddReport() {
      if (this.reportingEndMonthFC?.invalid) {
         this.reportingEndMonthFC.markAsTouched()
         this.reportingEndMonthFC.markAsDirty()
         return
      }

      let request: AppReportingPeriodRequest = {
         endMonth: +this.reportingEndMonthFC?.value
      }

      this.addReportClick.emit(request)
   }

   updateProjectClick() {
      if (!this.editProjectFrom?.valid) {
         this.editProjectFrom?.markAllAsTouched()
         if (this.editProjectFrom) {
            Object.keys(this.editProjectFrom.controls).forEach((key) => {
               this.editProjectFrom?.get(key)?.markAsDirty()
               this.editProjectFrom?.get(key)?.markAsTouched()
            })
         }

         return
      }
      let startDateRequest = this.startDateFC?.value ? (this._date.transform(this.startDateFC?.value, 'yyyy-MM-dd') ?? undefined) : undefined
      let durationRequest = +this.durationFC?.value

      let request: AppEditProjectRequest = {
         duration: durationRequest,
         fundingRate: this.foundingFC?.value ?? 0,
         callOfTheProposal: this.callOfTheProposalFC?.value ?? '',
         generalObjectives: this.generalObjectivesFC?.value ?? '',
         title: this.titleFC?.value.trim(),
         acronym: this.acronymFC?.value.trim(),
         typeOfAction: this.typeOfActionFC?.value,
         startDate: startDateRequest,
         currentMonth: this.currentMonthFC?.value,
         grantAgreement: +this.fundingAgreementIdFC?.value ? +this.fundingAgreementIdFC?.value : undefined
      }

      if (
         this.reportingPeriodsRes?.reportingPeriods?.length &&
         ((this.project?.startDate && startDateRequest !== this.project?.startDate) || (this.project?.duration && durationRequest !== this.project?.duration))
      ) {
         this._confirmationService.confirm({
            message: `This will delete all data in reporting period table. Are you sure?`,
            header: `Project duration has changed!`,
            icon: 'pi pi-info-circle',
            acceptButtonStyleClass: 'basic-alert-dialog-button-accept',
            accept: () => {
               this.updateProjectDetailClick.emit(request)
            }
         })
         return
      }

      this.updateProjectDetailClick.emit(request)
   }

   private initEditForm(project: AppProject) {
      this.foundingFC = new FormControl(project.fundingRate ?? 0, Validators.max(100))
      this.callOfTheProposalFC = new FormControl(project.callOfTheProposal ?? null, Validators.maxLength(2048))
      this.generalObjectivesFC = new FormControl(project.generalObjectives ?? null)
      this.titleFC = new FormControl({
         value: project.title ?? null,
         disabled: !!project.title && ($app.core.isRunningProject() || $app.core.isEndedProject())
      })
      this.acronymFC = new FormControl(
         { value: project.acronym ?? null, disabled: !!project.acronym && ($app.core.isRunningProject() || $app.core.isEndedProject()) },
         [Validators.required, Validators.maxLength(100)]
      )
      this.durationFC = new FormControl(project.duration ?? null, [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(180)])
      this.typeOfActionFC = new FormControl(
         { value: project.typeOfAction ?? null, disabled: !!project.typeOfAction && ($app.core.isRunningProject() || $app.core.isEndedProject()) },
         [Validators.required]
      )
      this.startDateFC = new FormControl(project.startDate, Validators.required)
      this.endDateFC = new FormControl(project.endDate)
      this.currentMonthFC = new FormControl(project.currentMonth ?? null)
      this.fundingAssociatedNameFC = new FormControl(project.grantAssociatedName ?? null)
      this.fundingAgreementIdFC = new FormControl(project.grantAgreement ?? null, [Validators.max(999999999999999), Validators.min(1)])

      this.editProjectFrom = new FormGroup({
         founding: this.foundingFC,
         callofTheProposal: this.callOfTheProposalFC,
         generalObjectives: this.generalObjectivesFC,
         title: this.titleFC,
         acronym: this.acronymFC,
         duration: this.durationFC,
         typeOfAction: this.typeOfActionFC,
         startDate: this.startDateFC,
         endDate: this.endDateFC,
         currentMonth: this.currentMonthFC,
         fundingAssociatedName: this.fundingAssociatedNameFC,
         fundingAgreementId: this.fundingAgreementIdFC
      })

      this.listener?.add(
         this.editProjectFrom.valueChanges.subscribe((v) => {
            this._hasUnsavedChanges = v && this.project && !this.areFormValuesEqualToProject(v, this.project)
            this.hasUnsavedChanges.emit(this._hasUnsavedChanges)
         })
      )
   }

   private areFormValuesEqualToProject(v: any, project: AppProject): boolean {
      if (!project) return false

      // Helper per confrontare date
      const compareDate = (a: any, b: any) => {
         if (!a && !b) return true
         if (!a || !b) return false
         const aStr = a instanceof Date ? a.toISOString().slice(0, 10) : new Date(a).toISOString().slice(0, 10)
         const bStr = b instanceof Date ? b.toISOString().slice(0, 10) : new Date(b).toISOString().slice(0, 10)
         return aStr === bStr
      }

      const checks = {
         founding: Number(v.founding) === Number(project.fundingRate),
         callofTheProposal: (v.callofTheProposal ?? '') === (project.callOfTheProposal ?? ''),
         generalObjectives: (v.generalObjectives ?? '') === (project.generalObjectives ?? ''),
         acronym: (v.acronym ?? project.acronym ?? '') === (project.acronym ?? ''),
         duration: Number(v.duration) === Number(project.duration),
         typeOfAction: (v.typeOfAction ?? project.typeOfAction ?? '') === (project.typeOfAction ?? ''),
         startDate: compareDate(v.startDate, project.startDate),
         currentMonth: (v.currentMonth ?? null) === (project.currentMonth ?? null),
         fundingAssociatedName: (v.fundingAssociatedName ?? '') === (project.grantAssociatedName ?? ''),
         fundingAgreementId: Number(v.fundingAgreementId) === Number(project.grantAgreement) || (!v.grantAgreementId && !project.grantAgreement)
      }

      return Object.values(checks).every(Boolean)
   }

   private initReportingPeriodForm() {
      this.reportingEndMonthFC = new FormControl(null, [
         Validators.required,
         Validators.min(this.reportingPeriodsRes?.nextStartMonth ? this.reportingPeriodsRes?.nextStartMonth + 1 : 2),
         Validators.max(this.project?.duration ?? 99)
      ])
      this.reportingNextPeriodFC = new FormControl(this.reportingPeriodsRes?.nextPeriodNumber ?? 1)
      this.reportingNextStartMonthFC = new FormControl(this.reportingPeriodsRes?.nextStartMonth ?? 1)

      this.listener?.add(
         this.reportingEndMonthFC.valueChanges.subscribe((v) => {
            if (v) this.hasUnsavedChanges.emit(true)
         })
      )
   }
}
